"use client"

import { create } from 'zustand'
import { persist } from 'zustand/middleware'

type Currency = 'RUB' | 'USD' | 'EUR'

interface CurrencyState {
  currency: Currency
  rates: Record<Currency, number>
  setCurrency: (c: Currency) => void
  formatPrice: (rubAmount: number) => string
  formatSecondary: (rubAmount: number) => string | null
}

export type { Currency }

export const useCurrencyStore = create<CurrencyState>()(
  persist(
    (set, get) => ({
      currency: 'RUB' as Currency,
      rates: { RUB: 1, USD: 0.011, EUR: 0.010 } as Record<Currency, number>,
      setCurrency: (c: Currency) => set({ currency: c }),
      formatPrice: (rubAmount: number): string => {
        const { currency, rates } = get()
        if (currency === 'RUB') {
          return `${rubAmount.toLocaleString('ru-RU')} ₽`
        }
        const converted = rubAmount * rates[currency]
        const symbol = currency === 'USD' ? '$' : '€'
        return `${symbol}${converted.toFixed(2)}`
      },
      formatSecondary: (rubAmount: number): string | null => {
        const { currency } = get()
        if (currency === 'RUB') return null
        return `≈ ${rubAmount.toLocaleString('ru-RU')} ₽`
      },
    }),
    {
      name: 'dveriant-currency',
      partialize: (state) => ({
        currency: state.currency,
        rates: state.rates,
      }),
      merge: (persisted, current) => {
        const validCurrencies: Currency[] = ['RUB', 'USD', 'EUR']
        return {
          ...current,
          ...persisted,
          currency:
            persisted.currency && validCurrencies.includes(persisted.currency)
              ? persisted.currency
              : current.currency,
          rates: persisted.rates ?? current.rates,
        }
      },
    }
  )
)
